import React from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { TYPE_ACCENT, TYPE_GLYPH, TYPE_LABELS, timeAgo, typeGlow } from "@/lib/format";

const SWIPE_THRESHOLD = 110;

export const InsightCard = ({ card, index, onDismiss, onDetail, onResolve }) => {
  const x = useMotionValue(0);
  const rightEdgeOpacity = useTransform(x, [15, 110], [0, 1]);
  const leftEdgeOpacity = useTransform(x, [-110, -15], [1, 0]);
  const accent = TYPE_ACCENT[card.type] || "#4D9CFF";
  const glyph = TYPE_GLYPH[card.type] || "\u25c6";

  const handleDragEnd = (_, info) => {
    if (info.offset.x > SWIPE_THRESHOLD) {
      onDismiss(card.id);
    } else if (info.offset.x < -SWIPE_THRESHOLD) {
      animate(x, 0, { type: "spring", stiffness: 400, damping: 30 });
      onDetail(card);
    } else {
      animate(x, 0, { type: "spring", stiffness: 400, damping: 30 });
    }
  };

  const runAction = (action) => {
    if (action.kind === "dismiss") onDismiss(card.id);
    else if (action.kind === "detail") onDetail(card);
    else onResolve(card.id, action.label);
  };

  return (
    <motion.div
      layout
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ x: 480, opacity: 0, transition: { duration: 0.22 } }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: index * 0.08 }}
      className="relative"
    >
      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.9}
        style={{ x, boxShadow: typeGlow(card.type) }}
        onDragEnd={handleDragEnd}
        data-testid={`insight-card-${index}`}
        data-insight-card="true"
        data-card-type={card.type}
        className="relative z-10 cursor-grab overflow-hidden rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4 active:cursor-grabbing"
        whileTap={{ scale: 0.995 }}
      >
        {/* swipe indicator: linee 2px sugli edge, visibili solo durante il drag */}
        <motion.span
          style={{ opacity: rightEdgeOpacity }}
          className="pointer-events-none absolute bottom-0 left-0 top-0 w-[2px] bg-[#00FF87]"
        />
        <motion.span
          style={{ opacity: leftEdgeOpacity }}
          className="pointer-events-none absolute bottom-0 right-0 top-0 w-[2px] bg-[#4D9CFF]"
        />

        {/* tag row */}
        <div className="flex items-center justify-between">
          <p className="caps-tag" style={{ color: accent, opacity: 0.75 }}>
            <span className="mr-1.5" data-testid={`insight-card-${index}-type-emoji`}>{glyph}</span>
            {TYPE_LABELS[card.type] || card.type}
            <span className="mx-1.5 text-white/20">·</span>
            <span className="text-white/30" style={{ letterSpacing: "0.12em" }}>{timeAgo(card.created_at)}</span>
          </p>
        </div>

        {/* numero chiave */}
        <p
          className="mono mt-3 text-[32px] font-light leading-none tracking-[-1px] tabular"
          style={{ color: accent }}
          data-testid={`insight-card-${index}-key-metric`}
        >
          {card.key_number}
        </p>

        {/* descrizione */}
        <p className="mt-2.5 text-sm font-light leading-[1.5] text-white/70" data-testid={`insight-card-${index}-title`}>
          {card.title}
        </p>
        {card.body && (
          <p className="mt-1 text-xs font-light leading-[1.5] text-white/40" data-testid={`insight-card-${index}-body`}>
            {card.body}
          </p>
        )}

        {/* ghost buttons */}
        <div className="mt-4 flex gap-2">
          {(card.actions || []).map((a, i) => (
            <button
              key={a.label}
              onClick={() => runAction(a)}
              data-testid={`insight-card-${index}-action-${i}`}
              data-action-kind={a.kind}
              className="h-9 flex-1 rounded-[6px] border border-white/[0.15] bg-transparent px-2 text-[11px] font-medium uppercase tracking-[0.18em] text-white/80 hover:bg-white/[0.05] active:scale-[0.98]"
            >
              {a.label}
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};
